import { FunctionComponent } from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { useTranslation } from "react-i18next";
import type { ManagementDisplayUnit } from "../typings/management-display-unit";
import { MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX } from "../utils/management-toolbar.constants";
import { ManagementToolbarField } from "./ManagementToolbarField";

const DISPLAY_UNIT_LABEL_ID = "management-display-unit-field-label";

type ManagementDisplayUnitToggleProps = {
  value: ManagementDisplayUnit;
  onChange: (unit: ManagementDisplayUnit) => void;
  /** When true, shows the shared toolbar field label (detail filter bars). */
  showLabel?: boolean;
};

/**
 * Switches management dashboard charts/KPIs between percent and count.
 */
export const ManagementDisplayUnitToggle: FunctionComponent<ManagementDisplayUnitToggleProps> = ({
  value,
  onChange,
  showLabel = false
}) => {
  const { t } = useTranslation();

  const groupAriaProps = showLabel
    ? { "aria-labelledby": DISPLAY_UNIT_LABEL_ID }
    : { "aria-label": t("_management_displayUnitGroup") };

  const toggle = (
    <ToggleButtonGroup
      value={value}
      exclusive
      size="small"
      {...groupAriaProps}
      onChange={(_, next: ManagementDisplayUnit | null) => {
        if (next) {
          onChange(next);
        }
      }}
      data-testid="management-display-unit-toggle"
      sx={{
        flexShrink: 0,
        width: showLabel ? "100%" : undefined,
        "& .MuiToggleButton-root": {
          height: MANAGEMENT_TOOLBAR_CONTROL_HEIGHT_PX,
          flex: showLabel ? 1 : undefined
        }
      }}
    >
      <ToggleButton
        value="percent"
        aria-label={t("_management_displayUnitPercent")}
        data-testid="management-display-unit-percent"
      >
        {t("Percent")}
      </ToggleButton>
      <ToggleButton
        value="count"
        aria-label={t("_management_displayUnitCount")}
        data-testid="management-display-unit-count"
      >
        {t("Count")}
      </ToggleButton>
    </ToggleButtonGroup>
  );

  if (showLabel) {
    return (
      <ManagementToolbarField label={t("_management_displayUnit")} labelId={DISPLAY_UNIT_LABEL_ID}>
        {toggle}
      </ManagementToolbarField>
    );
  }

  return toggle;
};
